import React, {useEffect, useRef, useState} from "react";
import FeatherIcon from "feather-icons-react";
import {matchPath, useHistory, useLocation} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {REPORT_TYPE, VIEW} from "../shared/util/enum";
import FormHandler from "../shared/util/form-handler";
import { DateRangePicker } from "rsuite";
import * as dateFns from "date-fns";
import {validateDepartmentSubmit} from "../shared/authentication/form-validation-rules";
import {
  getSelectedGraphType,
  getSelectedReportType,
  getSelectedShiftType,
  getSelectedShiftTypeGraph
} from "../shared/actions/report";
import {dateFormat, fetchFromLocalStorage, isFilterView} from "../shared/util/utils";

const REPORT_LIST_PATH = "/:departmentID/:machineID/report";
const REPORT_VIEW_PATHS = [
  "/:departmentID/:machineID/:performance/performanceGraph",
  "/:departmentID/:machineID/:performance/performanceTable",
];

const matchReportList = (pathname) => pathname ? matchPath(pathname, {path: REPORT_LIST_PATH, exact: true}) : null;
const matchReportView = (pathname) => pathname ? matchPath(pathname, {path: REPORT_VIEW_PATHS, exact: true}) : null;

const isSameMachine = (a, b) => a.params.departmentID === b.params.departmentID && a.params.machineID === b.params.machineID;

// Parse YYYY-MM-DD as a local date (new Date("YYYY-MM-DD") would be UTC midnight)
const parseLocalDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const ToggleButtons = () => {
  let history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [showToggle, setShowToggle] = useState(true);
  const [showFilter, setShowFilter] = useState(true);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const view = useSelector(state => {
    return state.setting.view
  });
  const selectedDept = useSelector(state => {
    return state.department.selectedDepartment;
  });
  const selectedMachine = useSelector(state => {
    return state.machine.selectedMachine;
  });
  const selectedReport = useSelector(state => {
    return state.reportList.selectedReport;
  });
  const selectedReportType = useSelector(state => {
    return state.reportList.selectedReportType;
  });
  const selectedShiftType = useSelector(state => {
    return state.reportList.selectedShiftType;
  });
  const selectedShiftTypeGraph = useSelector(state => {
    return state.reportList.selectedShiftTypeGraph;
  });
  const previousPathRef = useRef(null);
  const {values, handleChange, handleOnBlur, setValue} = FormHandler(toggleNavBar,validateDepartmentSubmit);

  const reportViewMatch = matchReportView(location.pathname);
  const todayDate = dateFormat(new Date());
  const selectedFromDate = (selectedReportType && selectedReportType.fromDate) || todayDate;
  const selectedToDate = (selectedReportType && selectedReportType.toDate) || todayDate;
  const activeShift = reportViewMatch ? selectedShiftTypeGraph : selectedShiftType;

  // Keep the "Today" placeholder unless a different range was carried over
  const pickerDefaultValue = selectedFromDate === todayDate && selectedToDate === todayDate
      ? undefined
      : [parseLocalDate(selectedFromDate), parseLocalDate(selectedToDate)];
  const depId = selectedDept.id ? selectedDept.id : fetchFromLocalStorage('depId');
  const machineId = selectedMachine.id ? selectedMachine.id : fetchFromLocalStorage('machineId');
  const departmentName = selectedDept.name ? selectedDept.name : fetchFromLocalStorage('firstNav');
  const machineName = selectedMachine.name ? selectedMachine.name : fetchFromLocalStorage('secondNav');
  const reportName = selectedReport ? selectedReport : fetchFromLocalStorage('thirdNav');

  useEffect(() => {
    if (isFilterView(location.pathname, VIEW.DEPARTMENT) || isFilterView(location.pathname, VIEW.MACHINE)) {
      setShowToggle(false);
      setShowFilter(false);
    }
    else if(isFilterView(location.pathname, VIEW.PERFORMANCE_GRAPH) || isFilterView(location.pathname, VIEW.PERFORMANCE_TABLE)){
      setShowFilter(true);
      setShowToggle(false);
    }
    else {
      setShowFilter(false);
      setShowToggle(true);
    }
  }, [location]);

  // Carry the dashboard filters into its graphs; reset every report filter on any other navigation
  useEffect(() => {
    const previousPath = previousPathRef.current;
    previousPathRef.current = location.pathname;
    const previousListMatch = matchReportList(previousPath);
    const previousViewMatch = matchReportView(previousPath);
    const currentViewMatch = matchReportView(location.pathname);

    if (currentViewMatch && previousViewMatch && isSameMachine(previousViewMatch, currentViewMatch)
        && previousViewMatch.params.performance === currentViewMatch.params.performance) {
      // Graph <-> table of the same report keeps every filter
      return;
    }

    if (currentViewMatch && previousListMatch && isSameMachine(previousListMatch, currentViewMatch)) {
      // Opened from a KPI card: graphs default to DAILY but keep the card's shift
      setValue({reportType: REPORT_TYPE.DAILY});
      dispatch(getSelectedGraphType(REPORT_TYPE.DAILY));
      dispatch(getSelectedShiftTypeGraph(selectedShiftType));
      return;
    }

    const todayRange = {
      fromDate: dateFormat(new Date()),
      toDate: dateFormat(new Date()),
    };
    setFilterResetKey(key => key + 1);
    setValue({reportType: REPORT_TYPE.DAILY});
    dispatch(getSelectedReportType(todayRange));
    dispatch(getSelectedGraphType(REPORT_TYPE.DAILY));
    dispatch(getSelectedShiftType("SHIFT_A"));
    dispatch(getSelectedShiftTypeGraph("SHIFT_A"));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  function onCheck(e) {
    const dates = {};
    dates.fromDate = dateFormat(e[0]);
    dates.toDate = dateFormat(e[1]);
    dispatch(getSelectedReportType(dates));
  }

  function onOpenFilter() {
    let doc = document.getElementsByClassName("rs-picker-daterange-panel");
    doc[0].scrollIntoView();
  }

  function onReportTypeChange(e) {
    handleChange(e);
    dispatch(getSelectedGraphType(e.target.value));
  }

  function getValueGraph() {
    const shiftGraphA = document.querySelector('input[name="shiftGraphA"]:checked') !==null && document.querySelector('input[name="shiftGraphA"]:checked').value;
    const shiftGraphB = document.querySelector('input[name="shiftGraphB"]:checked') !==null && document.querySelector('input[name="shiftGraphB"]:checked').value;

    const shifts = [shiftGraphA,shiftGraphB]
    let shift;
    if(shifts.includes(false)){
      shift = shifts.filter(s=>s!==false)[0]
    }
    else{
      shift="SHIFT_A_B"
    }

    if(shift && showFilter){
      dispatch(getSelectedShiftTypeGraph(shift));
    }else if(shift && showToggle){
      dispatch(getSelectedShiftType(shift));
    }
  }

  function toggleNavBar() {
    setIsLoading(true);
  }

  function goToHome() {
    history.push("/");
  }

  function goToMachine() {
    history.push("/" + depId + "/machine");
  }

  function goToReport() {
    history.push("/"+depId+"/"+machineId+"/report");
  }

  function goToGraph() {
    // selectedReport holds the breadcrumb display name, so reuse the route key already in the table URL
    if (isFilterView(location.pathname, VIEW.PERFORMANCE_TABLE)) {
      history.push(location.pathname.replace(/performanceTable$/, "performanceGraph"));
    }
  }

  function goToEmployee() {
    history.push("/employee");
  }

  const isEmployeeReport = location.pathname.startsWith("/employeeReport");

  return (
    <div className="map-toggle-btn" >
      {!isEmployeeReport && !["/employee", "/manualEntry", "/jobDetails"].includes(location.pathname) ?
      <div className="container  container-toggle-btn" >
          <div className="sa-bread-crumb-varna ">
            <div className="sa-bc-home sa-cursor"  onClick={()=>goToHome()} hidden={'/home' === location.pathname}>
              <FeatherIcon className={"icon-in-btn"} icon="home"/>
            </div>
            <div
                className={"sa-bc-farm " + (view === VIEW.MACHINE_LIST ? "sa-cursor" : "") + (view === VIEW.DEPT_LIST ? "sa-bc-right-border" : "")}
                onClick={() => goToMachine()} hidden={[VIEW.DEPT_LIST].includes(view)}>
              <FeatherIcon className={"fs-bc-arrow"} icon="chevron-right"/>
              <span className={"limit-toggle-name"}> {departmentName}</span>
            </div>
            <div
                className={"sa-bc-farm " + (view === VIEW.DEPT_LIST ? "sa-cursor" : "") + (view === VIEW.REPORT_LIST ? "sa-bc-right-border" : "")}
                onClick={() => goToReport()} hidden={[VIEW.DEPT_LIST,VIEW.MACHINE_LIST].includes(view)}>
              <FeatherIcon className={"fs-bc-arrow"} icon="chevron-right"/>
              <span className={"limit-toggle-name"}>{machineName}</span>
            </div>
            <div
                className={"sa-bc-farm " + (view ===  VIEW.GRAPH_LIST ? "sa-bc-right-border" : "") + (view ===  VIEW.MACHINE_LIST ? "sa-cursor" : "") + (view === VIEW.DEPT_LIST ? "sa-cursor" : "") + (view === VIEW.REPORT_LIST ? "sa-cursor" : "")}
                onClick={() => goToGraph()} hidden={[VIEW.DEPT_LIST, VIEW.MACHINE_LIST,VIEW.REPORT_LIST].includes(view)}>
              <FeatherIcon className={"fs-bc-arrow"} icon="chevron-right"/>
              <span className={"limit-toggle-name"}>{reportName}</span>
            </div>
          </div>
          <div className="spy-graph-btn row fs-filters" hidden={[VIEW.DEPT_LIST, VIEW.MACHINE_LIST].includes(view)}>
            <div className=" btn btn-sa-primary-shiftType" key={filterResetKey}>
                <label className="btn-radio-margin name-shift" >SHIFT A</label >
                <input className="btn-radio-margin"  type="checkbox"  name="shiftGraphA" value="SHIFT_A" id="myRadioA" onClick={getValueGraph} defaultChecked={["SHIFT_A", "SHIFT_A_B"].includes(activeShift)}/>
                <label className="btn-radio-margin name-shift" >SHIFT B</label>
                <input className="btn-radio-margin" type="checkbox" name="shiftGraphB" value="SHIFT_B" id="myRadioB" onClick={getValueGraph} defaultChecked={["SHIFT_B", "SHIFT_A_B"].includes(activeShift)}/>

            </div>
            <div className="datepicker-align" hidden={!showToggle && values.reportType !== REPORT_TYPE.CUSTOM}>
                <DateRangePicker
                  key={filterResetKey}
                  defaultValue={pickerDefaultValue}
                  disabledDate={date => date > new Date()}
                  disabled={false}
                  onChange={(e) => onCheck(e)}
                  oneTap={false} 
                  onOk={(e) => onCheck(e)}
                  className="name-shift"
                  showWeekNumbers={true} 
                  appearance={"default"}
                  placeholder={"Today"}
                  placement="bottomEnd"
                  onOpen={() => onOpenFilter()}
                  ranges={[
                    {
                      label: 'Today',
                      value: [new Date(), new Date()]
                    }, {
                      label: 'Yesterday',
                      value: [dateFns.addDays(new Date(), -1), dateFns.addDays(new Date(), -1)]
                    }, {
                      label: 'Last 7 days',
                      value: [dateFns.subDays(new Date(), 6), new Date()]
                    }, {
                      label: 'Last 30 days',
                      value: [dateFns.subDays(new Date(), 30), new Date()]
                    }
                  ]}
                />
            </div>
            <div className="datepicker-align" hidden={!showFilter}>
              <select
                  className={`form-control form-control-time-filter name-shift`}
                  name="reportType"
                  onBlur={handleOnBlur}
                  onChange={onReportTypeChange}
                  style={{width:130}}
                  value={values.reportType || ""}>
                  {
                    Object.values(REPORT_TYPE).map((item,index) => {
                      return <option key={index} value={item}>{item}</option>
                    })
                  }
              </select>
            </div>
          </div>
      </div>:
      <div className={`container container-toggle-btn ${isEmployeeReport && 'd-flex flex-row justify-content-between'}`}>
        <div className="sa-bread-crumb-varna">
            <div className={"sa-bc-farm sa-cursor sa-bc-right-border pl-1"} onClick={() => goToEmployee()} hidden={['/employee', '/manualEntry', '/jobDetails'].includes(location.pathname)}>
              <FeatherIcon className={"fs-bc-arrow"} icon="chevron-right" hidden={isEmployeeReport}/>
              <span className={"limit-toggle-name"}>Employee Performance</span>
            </div>
            <div className={"sa-bc-farm sa-cursor sa-bc-right-border"} hidden={['/employee', '/manualEntry', '/jobDetails'].includes(location.pathname)}>
              <FeatherIcon className={"fs-bc-arrow"} icon="chevron-right"/>
              <span className={"limit-toggle-name"}>Report</span>
            </div>
        </div>
        {
          isEmployeeReport &&
            <div className="sa-bread-crumb-varna d-flex flex-row">
              <select
                className={`form-control form-control-time-filter name-shift`}
                name="reportType"
                onBlur={handleOnBlur}
                onChange={onReportTypeChange}
                style={{width:130}}
                value={values.reportType || ""}>
                  {
                    Object.values(REPORT_TYPE).filter(item => item !== REPORT_TYPE.CUSTOM).map((item,index) => {
                      return <option key={index} value={item}>{item}</option>
                    })
                  }
              </select>
              <button className=" btn btn-sa-secondary-export-graph pis-add" onClick={()=>{}}>
                  <FeatherIcon className="spy-export-icon" icon="download" />
                  <span className={"desktop-show"}> EXPORT</span>
              </button>
          </div>
        }
      </div>
      }
    </div>
  )
};


export default ToggleButtons;
